import { useCallback, useEffect, useState } from "react";
import type { Lang } from "./types.ts";
import type { LoadedCatalog } from "./catalog.ts";
import { loadCatalog } from "./catalog.ts";
import type { ExamSession, ExamResult } from "./exam.ts";
import { useSettings, useStudyDoc } from "./state/store.ts";
import { useSync } from "./state/useSync.ts";
import { useKeyboard } from "./state/useKeyboard.ts";
import { useVersion, reloadToLatest } from "./state/useVersion.ts";
import { formatVersion } from "./version.ts";
import { useRoute } from "./router.ts";
import type { Route } from "./router.ts";
import { LANGS, t } from "./i18n/ui.ts";
import { htmlLangOf } from "./components/Localized.tsx";
import { LanguageControls } from "./components/LanguageControls.tsx";
import { ShortcutsDialog } from "./components/ShortcutsDialog.tsx";
import { NavDrawer } from "./components/NavDrawer.tsx";
import type { NavItem } from "./components/NavDrawer.tsx";
import { ExamRunner } from "./components/ExamRunner.tsx";
import { ResultsView } from "./components/ResultsView.tsx";
import { PracticeView } from "./components/PracticeView.tsx";
import { HomeView } from "./components/HomeView.tsx";
import { SheetsView } from "./components/SheetsView.tsx";
import { InfoPage } from "./components/InfoPage.tsx";
import { AccountView } from "./components/AccountView.tsx";
import { SettingsView } from "./components/SettingsView.tsx";

export function App() {
  const [catalog, setCatalog] = useState<LoadedCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  /** Result of the exam just finished; lives only for this visit. */
  const [lastExam, setLastExam] = useState<
    { session: ExamSession; result: ExamResult } | null
  >(null);
  /** Bumped only when a new exam is deliberately started, so that stepping
   *  through questions in the URL never re-deals the sheet. */
  const [examKey, setExamKey] = useState(0);

  const { route, navigate } = useRoute();
  const { settings, update, swapLanguages } = useSettings();
  const study = useStudyDoc();
  const version = useVersion();
  const sync = useSync(study.doc, study.setDoc);
  const lang = settings.primary;

  useEffect(() => {
    loadCatalog().then(setCatalog).catch((e) => setError(String(e)));
  }, []);

  // Keep the document language in sync so assistive tech picks the right voice.
  useEffect(() => {
    document.documentElement.lang = htmlLangOf(lang);
    document.title = t(lang, "appTitle");
  }, [lang]);

  /** Cycles the secondary language through the remaining ones, then off. */
  const cycleSecondary = useCallback(() => {
    const others = LANGS.map((l) => l.code).filter((c) => c !== settings.primary);
    const at = settings.secondary ? others.indexOf(settings.secondary) : -1;
    const next = at + 1 >= others.length ? null : others[at + 1] ?? null;
    update({ secondary: next as Lang | null });
  }, [settings, update]);

  const globalKeys = route.name === "home" || route.name === "results" ||
    route.name === "sheets" || route.name === "info" || route.name === "account";

  useKeyboard(
    {
      "?": () => setHelpOpen(true),
      s: swapLanguages,
      l: cycleSecondary,
      escape: () => navigate({ name: "home" }),
    },
    globalKeys
  );

  if (error) {
    return (
      <main className="shell">
        <p role="alert" className="error">{t(lang, "loadError")}<br />{error}</p>
      </main>
    );
  }
  if (!catalog) {
    return (
      <main className="shell">
        <p role="status" className="loading">{t(lang, "loading")}</p>
      </main>
    );
  }

  const go = (to: Route) => navigate(to);
  /** Exam and practice render the official green panel, which is its own chrome. */
  const onQuestionScreen = route.name === "exam" || route.name === "practice";
  const startExam = (setId?: number) => {
    setExamKey((n) => n + 1);
    // No question yet — ExamRunner pins one once the sheet is dealt.
    go({ name: "exam", setId });
  };

  const navSections: NavItem[][] = [
    [
      { id: "home", icon: "⌂", label: t(lang, "home"), onSelect: () => go({ name: "home" }) },
      { id: "new", icon: "30", label: t(lang, "newQuestions"),
        onSelect: () => go({ name: "practice", mode: { kind: "new" } }) },
      { id: "all", icon: "≡", label: t(lang, "allQuestionsTopics"),
        onSelect: () => go({ name: "practice", mode: { kind: "topic", categoryId: null } }) },
      { id: "sheets", icon: "▤", label: t(lang, "sampleSheets"),
        onSelect: () => go({ name: "sheets" }) },
    ],
    [
      { id: "account", icon: "☁", label: t(lang, "account"),
        note: sync.user?.username, onSelect: () => go({ name: "account" }) },
      { id: "settings", icon: "⚙", label: t(lang, "settings"),
        onSelect: () => go({ name: "settings" }) },
    ],
    [
      { id: "help", icon: "?", label: t(lang, "help"),
        onSelect: () => go({ name: "info", page: "help" }) },
      { id: "tips", icon: "✓", label: t(lang, "learningTips"),
        onSelect: () => go({ name: "info", page: "tips" }) },
      { id: "support", icon: "💬", label: t(lang, "support"),
        onSelect: () => go({ name: "info", page: "support" }) },
    ],
    [
      { id: "imprint", icon: "§", label: t(lang, "imprint"),
        onSelect: () => go({ name: "info", page: "imprint" }) },
    ],
  ];

  return (
    <>
      <a className="skiplink" href="#main">{t(lang, "skipToContent")}</a>

      {version.stale && (
        <div className="updatebar" role="status">
          <span>{t(lang, "updateAvailable")}</span>
          <button type="button" className="updatebar__btn" onClick={() => void reloadToLatest()}>
            {t(lang, "reloadNow")}
          </button>
        </div>
      )}

      {/* Menu screens get the app bar; question screens do not, matching the
          official mobile app — there the green question strip is the only
          chrome, and its ✕ is the way out. */}
      {!onQuestionScreen && (
        <header className="appbar">
          <button
            type="button"
            className="appbar__brand"
            onClick={() => go({ name: "home" })}
            aria-label={t(lang, "home")}
          >
            <span className="appbar__logo" aria-hidden="true">🚦</span>
            <strong>{t(lang, "appTitle")}</strong>
          </button>
          <button
            type="button"
            className="appbar__menu"
            onClick={() => setMenuOpen(true)}
            aria-label={t(lang, "openMenu")}
          >
            ☰
          </button>
        </header>
      )}


      <main
        id="main"
        className={`shell ${onQuestionScreen ? "shell--bare" : ""}`}
        tabIndex={-1}
      >
        {route.name === "home" && (
          <HomeView
            catalog={catalog}
            lang={lang}
            progress={study.progress}
            markedCount={study.marked.length}
            onExam={() => startExam()}
            onPractice={(mode) => go({ name: "practice", mode })}
            onSettings={() => go({ name: "settings" })}
            onReset={() => {
              if (confirm(t(lang, "resetProgressConfirm"))) study.reset();
            }}
          />
        )}

        {route.name === "exam" && (
          <ExamRunner
            key={examKey}
            catalog={catalog}
            settings={settings}
            setId={route.setId}
            question={route.question}
            onQuestionChange={(question) =>
              go({ name: "exam", setId: route.setId, question })
            }
            onPin={(dealtSetId, question) =>
              navigate(
                { name: "exam", setId: dealtSetId, question },
                { replace: true }
              )
            }
            isMarked={study.isMarked}
            onToggleMark={study.toggleMark}
            onFinish={(session, result) => {
              const wrong = new Set(result.wrong.map((w) => w.question.id));
              for (const item of session.items) {
                study.record(item.question.id, !wrong.has(item.question.id));
              }
              setLastExam({ session, result });
              go({ name: "results" });
            }}
            onExit={() => go({ name: "home" })}
            onSwapLanguages={swapLanguages}
            onCycleSecondary={cycleSecondary}
            onShowHelp={() => setHelpOpen(true)}
          />
        )}

        {route.name === "results" &&
          (lastExam ? (
            <ResultsView
              catalog={catalog}
              settings={settings}
              session={lastExam.session}
              result={lastExam.result}
              onNewExam={() => startExam()}
              onExit={() => go({ name: "home" })}
            />
          ) : (
            <p className="tempty">{t(lang, "noResults")}</p>
          ))}

        {route.name === "practice" && (
          <PracticeView
            // Remount on a mode switch so "30 new questions" deals a fresh
            // random block each time it is opened.
            key={route.mode.kind}
            catalog={catalog}
            settings={settings}
            progress={study.progress}
            mode={route.mode}
            onModeChange={(mode) => go({ name: "practice", mode })}
            question={route.question}
            onQuestionChange={(question, options) =>
              navigate({ name: "practice", mode: route.mode, question }, options)
            }
            marked={study.marked}
            isMarked={study.isMarked}
            onToggleMark={study.toggleMark}
            onRecord={study.record}
            onExit={() => go({ name: "home" })}
            onSwapLanguages={swapLanguages}
            onCycleSecondary={cycleSecondary}
            onShowHelp={() => setHelpOpen(true)}
          />
        )}

        {route.name === "settings" && <SettingsView lang={lang} catalog={catalog} />}

        {route.name === "sheets" && (
          <SheetsView
            catalog={catalog}
            lang={lang}
            progress={study.progress}
            onExam={(setId) => startExam(setId)}
            onPractice={(setId) => go({ name: "practice", mode: { kind: "sheet", setId } })}
          />
        )}

        {route.name === "account" && <AccountView lang={lang} sync={sync} />}

        {route.name === "info" && <InfoPage page={route.page} lang={lang} />}
      </main>

      <NavDrawer
        open={menuOpen}
        lang={lang}
        sections={navSections}
        onClose={() => setMenuOpen(false)}
        footer={
          <>
            <LanguageControls settings={settings} onChange={update} onSwap={swapLanguages} />
            <button
              type="button"
              className="drawer__item"
              onClick={() => {
                setMenuOpen(false);
                setHelpOpen(true);
              }}
            >
              <span className="drawer__icon" aria-hidden="true">⌨</span>
              <span className="drawer__label">{t(lang, "shortcutsTitle")}</span>
            </button>

            <p className="drawer__version">
              {t(lang, "version")} <span translate="no">{formatVersion(version.current)}</span>
              {version.stale ? ` · ${t(lang, "versionStale")}` : ""}
            </p>
          </>
        }
      />
      <ShortcutsDialog open={helpOpen} lang={lang} onClose={() => setHelpOpen(false)} />
    </>
  );
}
